import { createApi } from '@reduxjs/toolkit/query/react'
import { BasePageRequest, BasePageResponse, BaseRequest, BaseResponse } from '../models'
import { AiFeaturePreset } from '../../models/AiFeaturePreset'
import { AiFeature } from '../../models/AiFeature'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { AiResult, AiResultCreate, AiResultOverride, AiResultScorePayload } from '../../models/AiResult'

// Define a service using a base URL and expected endpoints
const _BASE_URL = import.meta.env.REACT_APP_AI_BASE_URL!

export interface GetPresetsRequest extends BasePageRequest {
  tag?: string | null
  ids?: string[]
  includeRestricted?: boolean
}

export interface GetPresetById extends BaseRequest {
  presetId: string
}

export interface GetFeatureByIdRequest extends BaseRequest {
  aiFeatureId: string
}

export interface GetFeaturesByFilters extends BasePageRequest {
  isActive?: boolean
  roomId?: string
  type?: string
  includeRestricted?: boolean
  aiFeaturePresetIds?: string
}

export interface UpdateInputParameterRequest {
  aiFeatureId: string
  accountId: string
  payload: UpdateInputParameterPayload
}

export interface UpdateInputParameterPayload {
  inputParameters: { [key: string]: any }
}

export interface SaveAiResultRequest {
  accountId: string
  payload: AiResultCreate
}

export interface OverrideAiResultRequest {
  accountId: string
  aiResultId: string
  payload: AiResultOverride
}

export interface ScoreAiResultRequest {
  accountId: string
  aiResultId: string
  payload: AiResultScorePayload
}

export interface GetAiResultsRequest extends BasePageRequest {
  roomId?: string
  sessionId?: string
  userId?: string | null
  presetId?: string
}

export const aiApi = createApi({
  reducerPath: 'aiApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['Features', 'Preset', 'PresetTag', 'PresetCount'],
  endpoints: (builder) => ({
    getPresets: builder.query<BasePageResponse<AiFeaturePreset[]>, GetPresetsRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/ai-feature-presets`,
          params: {
            page: request.pageRequest.page,
            size: request.pageRequest.size,
            tag: request.tag ?? '',
            ids: request.ids?.join() ?? '',
            includeRestricted: request.includeRestricted,
          },
        }
      },
      providesTags: ['Preset'],
    }),
    getPresetById: builder.query<BaseResponse<AiFeaturePreset>, GetPresetById>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/ai-feature-presets/${request.presetId}`,
        }
      },
    }),
    getPresetTags: builder.query<BasePageResponse<string[]>, BasePageRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/tags`,
          params: {
            page: request.pageRequest.page,
            size: request.pageRequest.size,
          },
        }
      },
      transformResponse: (response: BasePageResponse<string[]>) => {
        return { ...response, data: response.data.sort() }
      },
      providesTags: ['PresetTag'],
    }),

    getFeatureById: builder.query<BaseResponse<AiFeature>, GetFeatureByIdRequest>({
      query: (request) => `/accounts/${request.accountId}/ai-features/${request.aiFeatureId}`,
    }),

    updateInputParameters: builder.mutation<void, UpdateInputParameterRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/ai-features/${request.aiFeatureId}`,
        method: 'PATCH',
        body: {
          inputParameters: request.payload.inputParameters,
        },
      }),
      invalidatesTags: ['Features'],
    }),
    getFeatures: builder.query<BasePageResponse<AiFeature[]>, GetFeaturesByFilters>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/ai-features/`,
        params: {
          page: request.pageRequest.page,
          size: request.pageRequest.size,
          isActive: request.isActive,
          roomId: request.roomId,
          type: request.type,
          includeRestricted: request.includeRestricted,
          aiFeaturePresetIds: request.aiFeaturePresetIds,
        },
      }),
      providesTags: ['Features'],
    }),
    saveAiResult: builder.mutation<AiResult, SaveAiResultRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/ai-results`,
        method: 'POST',
        body: request.payload,
      }),
      transformResponse(response: BaseResponse<AiResult>) {
        return response.data
      },
    }),

    overrideAiResult: builder.mutation<AiResult, OverrideAiResultRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/ai-results/${request.aiResultId}/override`,
        method: 'POST',
        body: request.payload,
      }),
      transformResponse(response: BaseResponse<AiResult>) {
        return response.data
      },
    }),
    scoreAiResult: builder.mutation<AiResult, ScoreAiResultRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/ai-results/${request.aiResultId}/score`,
        method: 'POST',
        body: request.payload,
      }),
      transformResponse(response: BaseResponse<AiResult>) {
        return response.data
      },
    }),
    getAiResults: builder.query<BasePageResponse<AiResult[]>, GetAiResultsRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/ai-results`,
          params: {
            page: request.pageRequest.page,
            size: request.pageRequest.size,
            userId: request.userId,
            roomId: request.roomId,
            sessionId: request.sessionId,
            presetId: request.presetId,
          },
        }
      },
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetPresetsQuery,
  useGetPresetByIdQuery,
  useLazyGetPresetsQuery,
  useUpdateInputParametersMutation,
  useGetPresetTagsQuery,
  useGetFeaturesQuery,
  useLazyGetFeaturesQuery,
  useLazyGetPresetByIdQuery,
  useGetFeatureByIdQuery,
  useLazyGetFeatureByIdQuery,
  useSaveAiResultMutation,
  useOverrideAiResultMutation,
  useScoreAiResultMutation,
  useGetAiResultsQuery,
} = aiApi
